import { Inbox, Pencil, Trash2, Paperclip, Search, Eye, Landmark } from 'lucide-react'
import { Button, Card, Select, Input, DateInput } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { PermissionGate } from '@/components/common/PermissionGate'
import {
  expenseCategoryLabel,
  paymentMethodLabel,
  formatExpenseAmount,
  formatExpenseDate,
} from './expenseUtils'
import { PREDEFINED_EXPENSE_CATEGORIES } from '@/features/expenses/types/expense.types'
import type { ExpenseEntry } from '@/features/expenses/types/expense.types'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { ORGANIZATION_LABEL } from './expenseScope'

export interface ExpenseFilters {
  search: string
  category: string
  dateFrom: string
  dateTo: string
  amountMin: string
  amountMax: string
}

const CATEGORY_OPTIONS: SelectOption[] = [
  { value: '', label: 'All categories' },
  ...PREDEFINED_EXPENSE_CATEGORIES.map((c) => ({ value: c, label: expenseCategoryLabel(c) })),
]

interface ExpenseTableProps {
  entries: ExpenseEntry[]
  isLoading: boolean
  error: Error | null
  filters: ExpenseFilters
  onFiltersChange: (patch: Partial<ExpenseFilters>) => void
  onRetry: () => void
  onEdit: (entry: ExpenseEntry) => void
  onDelete: (entry: ExpenseEntry) => void
  onViewReceipts: (entry: ExpenseEntry) => void
  companyNameById?: Map<string, string>
  emptyMessage?: string
}

export function ExpenseTable({
  entries,
  isLoading,
  error,
  filters,
  onFiltersChange,
  onRetry,
  onEdit,
  onDelete,
  onViewReceipts,
  companyNameById,
  emptyMessage = 'No expenses match the current filters.',
}: ExpenseTableProps): React.ReactElement {
  return (
    <Card
      contentClassName="p-0"
      header={
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <Input
              value={filters.search}
              onChange={(e) => onFiltersChange({ search: e.target.value })}
              placeholder="Search by title…"
              aria-label="Search expenses"
              className="pl-9"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              Category
              <Select
                options={CATEGORY_OPTIONS}
                value={filters.category}
                onChange={(v) => onFiltersChange({ category: v })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              From
              <DateInput
                value={filters.dateFrom}
                onChange={(e) => onFiltersChange({ dateFrom: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              To
              <DateInput
                value={filters.dateTo}
                onChange={(e) => onFiltersChange({ dateTo: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              Min amount
              <Input
                type="number"
                min="0"
                step="0.01"
                value={filters.amountMin}
                onChange={(e) => onFiltersChange({ amountMin: e.target.value })}
                placeholder="0"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              Max amount
              <Input
                type="number"
                min="0"
                step="0.01"
                value={filters.amountMax}
                onChange={(e) => onFiltersChange({ amountMax: e.target.value })}
                placeholder="∞"
              />
            </label>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <SectionLoader variant="table" cols={7} rows={6} label="Loading expenses" />
      ) : error ? (
        <QueryErrorState
          error={error}
          title="Couldn't load expenses"
          onRetry={onRetry}
          className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
        />
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Inbox className="h-12 w-12 text-gray-400 dark:text-gray-500" aria-hidden />
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">No expenses</h3>
          <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">{emptyMessage}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm" aria-label="Expenses">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                  Title
                </th>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                  Category
                </th>
                {companyNameById && (
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                    Belongs to
                  </th>
                )}
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                  Date
                </th>
                <th scope="col" className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                  Method
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">
                  Amount
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry._id}
                  className="border-b border-gray-200 last:border-0 dark:border-gray-700"
                >
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{entry.title}</span>
                      {entry.attachments && entry.attachments.length > 0 && (
                        <Paperclip
                          className="h-3.5 w-3.5 text-gray-400"
                          aria-label={`${entry.attachments.length} attachment(s)`}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {expenseCategoryLabel(entry.category)}
                  </td>
                  {companyNameById && (
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {entry.companyId ? (
                        (companyNameById.get(entry.companyId) ?? '—')
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                          <Landmark className="h-3.5 w-3.5" aria-hidden />
                          {ORGANIZATION_LABEL}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {formatExpenseDate(entry.date)}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {paymentMethodLabel(entry.paymentMethod)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                    {formatExpenseAmount(entry.amount, entry.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {entry.attachments && entry.attachments.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 px-2 text-xs"
                          onClick={() => onViewReceipts(entry)}
                          aria-label={`View receipts for ${entry.title}`}
                          title="View receipts"
                        >
                          <Eye className="h-4 w-4" aria-hidden />
                        </Button>
                      )}
                      <PermissionGate module="expenses" action="update">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 px-2 text-xs"
                          onClick={() => onEdit(entry)}
                          aria-label={`Edit ${entry.title}`}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                      </PermissionGate>
                      <PermissionGate module="expenses" action="delete">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 px-2 text-xs text-danger hover:text-danger"
                          onClick={() => onDelete(entry)}
                          aria-label={`Delete ${entry.title}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

ExpenseTable.displayName = 'ExpenseTable'
